import React, { useState, useEffect, useRef }  from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from "react-i18next";

import { formatPrice, getIncentiveDisplayName, getMazdaFeesAndLeviesDetailTitle, convertNum, getProvince, getLang} from '../../../utils'
import { openModal } from '../../../Helpers/helpers';

//import SummaryCalculator from '../../../Helpers/summarycalculator';
import { SummaryConstants as Constants } from '../../../Helpers/summaryconstants';
import GTMHelper from '../../../Helpers/gtmHelper';

import { Tooltip } from '../../1_Atoms/Tooltip/Tooltip';
import { Button } from '../../1_Atoms/Button/Button';
import { OcaModal } from '../../3_Organisms/Modal/OcaModal';
import CollapseLease from '../Collapse/CollapseLease';
import CollapseLeaseMileage from '../Collapse/CollapseLeaseMileage';

export const LeaseOptions = ({ setSelectedLeaseOption, includeFees, includeTaxes, includeRebates, setSelectedLeasesMileage, setPreorderModalOpen, ...props }) => {
    const navigate = useNavigate();
    const { year, carline, trim, model, packageCode } = useParams();
    const summaryCalculator = window.summaryCalculator;
    
    const { t, i18n } = useTranslation(['payments', 'general', 'summary']);
    const provinceCode = getProvince().toUpperCase();
    const [financialInfo, setFinancialInfo] = useState(summaryCalculator.FinancialInfo);
    const [update, setForceUpdate] =  useState(''); 
    const [isLeaseTermCollapsed, setLeaseTermCollapsed] = useState(false);

    const [didInit, setInit] = useState(false);
    const [showDetailedPricing, toggleShowDetailedPricing] = useState(false);
    const leaseDetailRef1 = useRef();
    const leaseDetailRef2 = useRef();
    const leaseDetailRef3 = useRef();

	const gtmHelper = new GTMHelper();
    
    const [ocaOpen, setOcaModalOpen] = useState(false);
    const [financeType, setFinanceType] = useState(false);

    //Fixed Number provided by API
    const [currentIncentives, setCurrentIncentives] = useState(null);
    
    const [reserveOpen, setReserveModalOpen] = React.useState(false);

    useEffect(() =>{
        const collapse1 = leaseDetailRef1.current;
        const collapse2 = leaseDetailRef2.current;
        const collapse3 = leaseDetailRef3.current;
        
        if(typeof window.mzAnimator === 'object' && !didInit && showDetailedPricing){
            window.mzAnimator.animateHeight(collapse1, 'CollapseDetailedLease1', 'init')
            window.mzAnimator.animateHeight(collapse2, 'CollapseDetailedLease2', 'init')
            window.mzAnimator.animateHeight(collapse3, 'CollapseDetailedLease3', 'init')
            setInit(true);
            toggleCollapse(false);
        }else{
            toggleCollapse(true);
        }
    },[showDetailedPricing, didInit]);

 
    function toggleCollapse(fromClick) {
        const collapse1 = leaseDetailRef1.current;
        const collapse2 = leaseDetailRef2.current;
        const collapse3 = leaseDetailRef3.current;
            
        if(showDetailedPricing){
            window.mzAnimator.animateHeight(collapse1, 'CollapseDetailedLease1', true)
            window.mzAnimator.animateHeight(collapse2, 'CollapseDetailedLease2', true)
            window.mzAnimator.animateHeight(collapse3, 'CollapseDetailedLease3', true)
        }else{
            window.mzAnimator.animateHeight(collapse1, 'CollapseDetailedLease1', false)
            window.mzAnimator.animateHeight(collapse2, 'CollapseDetailedLease2', false)
            window.mzAnimator.animateHeight(collapse3, 'CollapseDetailedLease3', false)
        }
        if(fromClick)
            gtmHelper.setGTM({'action':'Collapse', 'type': 'Detailed Lease ', 'value': showDetailedPricing}, true , false);
        
    }
    function financeClick(financeType){
        document.querySelector('body').classList.add('disabled');
		setOcaModalOpen(true);
        setFinanceType(financeType);
        
        const price = summaryCalculator.FinancialInfo.calculatedLease.balanceToLeaseWithFeesWithRebatesTaxed;
        gtmHelper.setGTM({'action':`OCA - Lease Payment`,'year': year,'model': ReactHtmlParser(carline), 'trim': trim, 'price' : Math.round(price)}, true, false);
    }

    function getRebatesDetails() {
		return financialInfo !== null ? financialInfo.calculatedLease.evRebates.details.map((rebate, index) => `<li key="${index}" class="finance-list__item rebate">
                	<p class="desc text--green">${rebate.title==='federal'? t('fed_rebate'):t('prov_rebate') + ' ('+getProvince()+')'}</p><span class="price text--green">${rebate.lease ? '-' : ''}${formatPrice(rebate.lease, true)}</span>
                </li>`).join("") : '';
    }

    function getTaxRebatesDetails() {
        return <>
            <li class="finance-list__item"><p class="desc">{t('tax_fed_rebate')}</p><span class="price">{formatPrice(financialInfo.calculatedLease.evRebates.total.lease_federal_tax, true)}</span></li>
            <li class="finance-list__item"><p class="desc">{t('tax_prov_rebate') + ' ('+getProvince()+')'}</p><span class="price">{formatPrice(financialInfo.calculatedLease.evRebates.total.lease_provincial_tax, true)}</span></li>
        </>
    }

    function getOptionList() {
        let optionList = '';
        
        const options = summaryCalculator.FinancialInfo ?.calculatedLease ?.options;

        if(options.exteriorColour.price >0){
            optionList += ` <li class="finance-list__item">
                <p class="desc">${t('exterior')} ${options.exteriorColour.description}</p>
                <span class="price">${formatPrice(options.exteriorColour.price, true)}</span>
            </li>`
        }
        if(options ?.interiorColour.price >0){
            optionList += ` <li class="finance-list__item">
                <p class="desc">${t('interior')} ${options.interiorColour.description}</p>
                <span class="price">${formatPrice(options.interiorColour.price, true)}</span>
            </li>`
        }
        if(options ?.model_option_package ?.financial ?.msrp >0){
            optionList += ` <li class="finance-list__item">
                <p class="desc">${t('package')} ${options.model_option_package.name}</p>
                <span class="price">${formatPrice(options.model_option_package.financial.msrp, true)}</span>
            </li>`
        }

        const selectedWarrantyPackage = JSON.parse(sessionStorage.getItem('selectedWarrantyPackage'));
        if(selectedWarrantyPackage != null && selectedWarrantyPackage.name != undefined ){
            const warrantyPrice = (JSON.parse(sessionStorage.getItem('selectedWarrantyPackage')).financial.msrp);
            optionList += ` <li class="finance-list__item">
                <p class="desc">${t("warranties")} ${selectedWarrantyPackage.name}</p>
                <span class="price">${formatPrice(warrantyPrice, true)}</span>
            </li>`
        }   
        
        const selectedConnectedVehiclePackage = JSON.parse(sessionStorage.getItem('selectedConnectedVehiclePackage'));
        if(selectedConnectedVehiclePackage != null && selectedConnectedVehiclePackage.name != undefined){
            const connectedPrice = (JSON.parse(sessionStorage.getItem('selectedConnectedVehiclePackage')).financial.msrp);
            optionList += ` <li class="finance-list__item">
                <p class="desc">${t("connected")} <span style="text-transform:lowercase">${selectedConnectedVehiclePackage.name}</span></p>
                <span class="price">${formatPrice(connectedPrice, true)}</span>
            </li>`
        }

        if(options.wheels.price >0){
            const options = summaryCalculator.FinancialInfo ?.calculatedFinance ?.options;

            optionList += `  <li class="finance-list__item">
                <p class="desc">${`4x ${options.wheels.title}`}</p>
                <span class="price">${formatPrice(options.wheels.base_msrp, true)}</span>
            </li>
            <li class="finance-list__item">
                <p class="desc">${`4x ${t("payments:tpms")}`}</p>
                <span class="price">${formatPrice(options.wheels.tpms, true)}</span>
            </li>
            <li class="finance-list__item">
                <p class="desc">${t("payments:installation")}</p>
                <span class="price">${formatPrice(options.wheels.install_cost, true)}</span>
            </li>`
        }

        return optionList;
    }    

    function getAccessoriesDetails() {
        let accessories = sessionStorage.getItem(Constants.SelectedAccessories);
        accessories = JSON.parse(accessories);        
        
        const accList = accessories.map((accessory, index) => {
            return(
                <>  
                    <li className="finance-list__item financeOptions_accessories">
                        <p className="desc">{accessory.name}</p>
                        <span className="price">{formatPrice(accessory.base_msrp, true)}</span>
                    </li>
                    <li className="finance-list__item financeOptions_accessories">
                        <p className="desc">{accessory.name} - Installation</p>
                        <span className="price">{formatPrice(accessory.install_cost, true)}</span>
                    </li>
                </>
            )
        });
        return accList;
    }

    const handleContactDealerLinkClick = (e) => {
        e.preventDefault();

        const contactRoute = `/${year}/${carline}/${trim}/${model}/${packageCode}/summary?contactDealer`;
        navigate(contactRoute);
    }
    
    useEffect(() =>{
        (async function() {
            setInit(false);
            
            setFinancialInfo(await summaryCalculator.RecalculateFinancialInfo());

            setForceUpdate(Math.random());
            
        })();
    },[summaryCalculator._tradeInValue, summaryCalculator._downPaymentValue, summaryCalculator.LoyaltyChecked, summaryCalculator.GraduateChecked, summaryCalculator.MilitaryChecked, summaryCalculator.FirstTimeOwnerChecked, summaryCalculator.IncludeFees, summaryCalculator.IncludeTaxes, summaryCalculator.AnnualMileage, summaryCalculator.IncludeRebates]);

    useEffect(() => {
        if(summaryCalculator.FinancialInfo.incentive_programs) 
            setCurrentIncentives(summaryCalculator.FinancialInfo.incentive_programs?.find(element => element.type === "incentive"));

        console.log('refreshing the lease options section');
        //console.log(summaryCalculator.FinancialInfo);
    }, [update]);
    
    includeFees = summaryCalculator.IncludeFees;
    includeTaxes = summaryCalculator.IncludeTaxes;
    includeRebates = summaryCalculator.IncludeRebates;
	
    const leaseDropdown = async (isOpen) => {
        if(didInit){
            const collapse1 = leaseDetailRef1.current;
            const collapse2 = leaseDetailRef2.current;
            const collapse3 = leaseDetailRef3.current;
        
            window.mzAnimator.animateHeight(collapse1, 'CollapseDetailedLease1', 'init')
            window.mzAnimator.animateHeight(collapse2, 'CollapseDetailedLease2', 'init')
            window.mzAnimator.animateHeight(collapse3, 'CollapseDetailedLease3', 'init')
            setInit(true);
            toggleCollapse(false);
        }
    }

    const leaseTermSelect = async (e) => {
        setFinancialInfo(await summaryCalculator.RecalculateFinancialInfo());
    }

    const leaseMileageItemClick = async (e)=>{
        const annualMileageValue = parseInt(e.currentTarget.querySelector('input[name="annualMileage"]').value);
        summaryCalculator.AnnualMileage = annualMileageValue;

        setFinancialInfo(await summaryCalculator.RecalculateFinancialInfo());

        setSelectedLeasesMileage(annualMileageValue);
    }

    const getMazdaFeesAndLeviesDetails = () => {
        const feesAndLeviesToDisplay = summaryCalculator.FinancialInfo.calculatedLease.feesAndLevies.detail.filter(item => item.title !== 'PDE');
        
        return feesAndLeviesToDisplay;
    }

    const getDisplayedFee = (fee) => {
        return getMazdaFeesAndLeviesDetailTitle(fee.title, t);
    }

    async function handleReserveYoursClick() {
        const isRetailerMode = await summaryCalculator.isRetailerMode();
        summaryCalculator.CurrentPurchaseMethod = 'Lease';
        if (isRetailerMode) {
                setReserveModalOpen(true);
        } else {
            openGetLocal();
        }
    }

    function handlePreOrderClick() {
        document.querySelector('body').classList.add('disabled');
        setPreorderModalOpen(true)
    }


    function openGetLocal(){
        const lang = getLang();
        gtmHelper.setGTM({'action':'Open Modal','modal': 'Contact Dealer'}, true, false);

        const targetModal = document.getElementById('mz-react-send-build-to-dealer-modal');
        if (targetModal && summaryCalculator) {
            if (summaryCalculator.SelectedColourExteriorIndex !== 'null' && summaryCalculator.Colours !== null) {
                const heading = targetModal.querySelector('.mz-send-build-to-dealer__vehicle-name');
                if (heading) {
                    heading.textContent = lang === 'en' ? summaryCalculator._selectedModelYear.year.title + ' ' + ReactHtmlParser(summaryCalculator._selectedModelYear.carline.title) : ReactHtmlParser(summaryCalculator._selectedModelYear.carline.title) + ' ' + summaryCalculator._selectedModelYear.year.title;
                }
            }
            openModal('mz-react-send-build-to-dealer-modal');
        }
    }


    const firstPaymentTaxes = summaryCalculator.FinancialInfo.calculatedLease.leaseValues[summaryCalculator.LeaseTermIndex].monthlyWithTaxes - (summaryCalculator.FinancialInfo.calculatedLease.leaseValues[summaryCalculator.LeaseTermIndex].monthlyWithTaxes / (1 + summaryCalculator.FinancialInfo.calculatedLease.tax));

    const hasResponseError = summaryCalculator.getResponsesHasError();

    function getWeeklyPrice() {
        if (hasResponseError)
            return formatPrice(0);
        else
            return formatPrice(summaryCalculator.FinancialInfo.calculatedLease?.leaseValues[summaryCalculator.LeaseTermIndex][summaryCalculator.IncludeTaxes ? 'weeklyWithTaxes' : 'weekly'])
    }

    function getBiWeeklyPrice() {
        if (hasResponseError)
            return formatPrice(0);
        else
            return formatPrice(summaryCalculator.FinancialInfo.calculatedLease?.leaseValues[summaryCalculator.LeaseTermIndex][summaryCalculator.IncludeTaxes ? 'biWeeklyWithTaxes' : 'biWeekly'])
    }

    function getMonthlyPrice() {
        if (hasResponseError)
            return formatPrice(0);
        else
            return formatPrice(summaryCalculator.FinancialInfo.calculatedLease?.leaseValues[summaryCalculator.LeaseTermIndex][summaryCalculator.IncludeTaxes ? 'monthlyWithTaxes' : 'monthly']);
    }

    function getDisplayedRate() {
        if (hasResponseError)
            return '-';

        const leaseElement = summaryCalculator.FinancialInfo.calculatedLease.leaseValues[summaryCalculator.LeaseTermIndex];
        if (Number.parseFloat(leaseElement.leaseRate) !== Number.parseFloat(leaseElement.leaseRateDiscounted))
            return convertNum(leaseElement.leaseRateDiscounted, 2) + '%';
        else
            return convertNum(leaseElement.leaseRate,2) + '%';
    }

    function getInformationalRate() {
        if (hasResponseError)
            return '-';
        else
            return convertNum(summaryCalculator.getLeaseInformationalAprRate(),2) + '%';
    }

    function getTotalPayable() {
        //{includeFees && includeTaxes && includeRebates 
        let totalPayable = summaryCalculator.FinancialInfo.calculatedLease.balanceToLease;
        totalPayable = includeFees ? summaryCalculator.FinancialInfo.calculatedLease.balanceToLeaseWithFees : totalPayable;
        totalPayable = includeRebates ? summaryCalculator.FinancialInfo.calculatedLease.balanceToLeaseWithRebates : totalPayable;
        totalPayable = includeFees && includeRebates ? summaryCalculator.FinancialInfo.calculatedLease.balanceToLeaseWithFeesWithRebates : totalPayable;

        return hasResponseError ? '-' :formatPrice(totalPayable, true);
    }

    function getFirstPayment() {
        const firstPayment = includeTaxes ? (summaryCalculator.FinancialInfo.calculatedLease.leaseValues[summaryCalculator.LeaseTermIndex].monthlyWithTaxes - firstPaymentTaxes) : summaryCalculator.FinancialInfo.calculatedLease.leaseValues[summaryCalculator.LeaseTermIndex].monthly;

        return hasResponseError ? '-' :formatPrice(firstPayment, true);
    }

    function getTotalDue() {
        const leaseElement = summaryCalculator.FinancialInfo.calculatedLease.leaseValues[summaryCalculator.LeaseTermIndex];
        
        const totalDue = (includeTaxes ? (leaseElement.monthlyWithTaxes + summaryCalculator.FinancialInfo.calculatedLease.downPaymentValue + summaryCalculator.FinancialInfo.calculatedLease.downPaymentTax) : leaseElement.monthly  + summaryCalculator.FinancialInfo.calculatedLease.downPaymentValue)
                        + (includeRebates && includeTaxes ? (summaryCalculator.FinancialInfo.calculatedLease.evRebates.total.lease_federal_tax + summaryCalculator.FinancialInfo.calculatedLease.evRebates.total.lease_provincial_tax) : 0);

        return hasResponseError ? '-' :formatPrice(totalDue, true);
    }


    return (
            <div className="mz-lease-options lease">
            <h4>{t("lease_options")}</h4>
            {!summaryCalculator.getResponsesHasError() ?
            <>
            <div className="mz-lease__count">
                    <div className="mz-lease__count-item ">
                    <p>{t("weekly")}</p>
                    <span>{getWeeklyPrice()}</span> 
                </div>
                <div className="mz-lease__count-item ">
                    <p>{t("bi_weekly")}</p>
                    <span>{getBiWeeklyPrice()}</span> 
                </div>
                <div className="mz-lease__count-item ">
                    <p>{t("monthly")}</p>
                    <span>{getMonthlyPrice()}</span> 
                </div>
            </div>
            <div className="mz-lease__terms">
                <CollapseLease dropdownCallback={leaseDropdown} selectCallback={leaseTermSelect} setSelectedLeaseOption={setSelectedLeaseOption} />
                <CollapseLeaseMileage callback={leaseMileageItemClick} setSelectedLeasesMileage={setSelectedLeasesMileage} />
            </div>
            </> 
            :
            <>
                <p className="text--center">
                    {ReactHtmlParser(t("general:generalErrorMessage"))}
                </p>
            </>}            
            <div className="hr light"></div>
            <div className={`mz-finance__numbs ${!showDetailedPricing ? 'collapsed' : 
            ''}`}>
                <ul className="finance-list hidden" ref={leaseDetailRef1}>
                    <li className="finance-list__item">
                        <p className="desc">{t("msrp")}</p>
                        <span className="price">{`${formatPrice(summaryCalculator.SelectedModel.financial.msrp, true)}`}</span>
                    </li>
                    <li className="finance-list__item">
                        <p className="desc">{t("trade_in")}<sup>1</sup></p>
                        <span className="price discount">{`${summaryCalculator.FinancialInfo.tradeInValue > 0 ? '-' : ''}${formatPrice(summaryCalculator.FinancialInfo.tradeInValue, true)}`}</span>
                    </li>
                    {currentIncentives && currentIncentives.value >0 ?
                    <li className="finance-list__item">
                        <p className="desc">{t("current_incentive")} <Tooltip TooltipName="incentives" incentives={currentIncentives.value}/></p>
                        <span className="price discount">{currentIncentives?'-':''}{formatPrice(currentIncentives.value, true)}</span>
                    </li>:''}
                    {ReactHtmlParser(getOptionList())}
                    {summaryCalculator.FinancialInfo.calculatedLease?.accessories.total > 0?
                        getAccessoriesDetails()
                    :''}
                    <li className="finance-list__item">
                        <p className="desc">{t("freight")}</p>
                        <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.freight, true)}`}</span>
                    </li>
                    <li className="finance-list__item">
                        <p className="desc">{t("general:pde")}</p>
                        <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.pde, true)}`}</span>
                    </li>
                    {includeFees ? <li className="finance-list__item">
                        <p className="desc">
                            {getDisplayedFee({title: "Administration Fee"})}
                            <sup>9</sup>
                        </p>
                        <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.adminServiceFees, true)}`}</span>
                    </li>:''}                       
                    {includeFees? getMazdaFeesAndLeviesDetails().map((fee, index) => {
                        return <li className="finance-list__item">
                            <p className="desc">{ReactHtmlParser(getDisplayedFee(fee))}
                                {fee.sup ? ReactHtmlParser(`<sup>${fee.sup}</sup>`) : ''}
                            </p>
                            <span className="price">{`${formatPrice(fee.price, true)}`}</span>
                        </li>
                    }):''}
					{includeRebates ? ReactHtmlParser(getRebatesDetails()) : ""}
                </ul>
                <ul className="finance-list hidden" ref={leaseDetailRef2}>
                    <li className="finance-list__item"><p className="desc">{provinceCode === "QC" ? "RDPRM" : "PPSA"}<sup>7</sup></p>
                        <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.leaseValues[summaryCalculator.LeaseTermIndex].ppsa, true)}`}</span>
                    </li>
                    <li className="finance-list__item">
                        <p className="desc">{t("sub_total")}</p>
                        <span className="price">
                            { includeFees &&  includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.subtotalWithFeesWithRebates, true)}`}</span> : null}
                            { includeFees && !includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.subtotalWithFees, true)}`}</span> : null}
                            {!includeFees &&  includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.subtotalWithRebates, true)}`}</span> : null}
                            {!includeFees && !includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.subtotal, true)}`}</span> : null}
                        </span>
                    </li>
                    <li className="finance-list__item">
                        <p className="desc">{t("down_payment")}<sup>2</sup></p>
                        <span className="price discount">{`${summaryCalculator.FinancialInfo.downPaymentValue > 0 ? '-' : ''}${formatPrice(summaryCalculator.FinancialInfo.downPaymentValue, true)}`}</span>
                    </li>
                    {summaryCalculator.FinancialInfo.calculatedLease.rewards.map((el, index) => {
                        return <li className="finance-list__item" key={index}>
                            <p className="desc">{getIncentiveDisplayName(el.type)}</p>
                            <span className="price discount">{`-${el.cash_value?formatPrice(el.cash_value, true):formatPrice(el.value, true)}`}</span>
                        </li>
                    })}

                </ul>
                <ul className={`finance-list  ${showDetailedPricing ? 'finance-list__border' : ''}`}>
                    <li className="finance-list__item">
                        <p className="desc">{t("balance_to_lease")}</p>
                            {includeFees && includeTaxes && includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.balanceToLeaseWithFeesWithRebates, true)}`}</span> : null}
                            {!includeFees && includeTaxes && includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.balanceToLeaseWithRebates, true)}`}</span> : null}
                            {includeFees && !includeTaxes && includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.balanceToLeaseWithFeesWithRebates, true)}`}</span> : null}
                            {includeFees && includeTaxes && !includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.balanceToLeaseWithFees, true)}`}</span> : null}
                            {includeFees && !includeTaxes && !includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.balanceToLeaseWithFees, true)}`}</span> : null}
                            {!includeFees && includeTaxes && !includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.balanceToLease, true)}`}</span> : null}
                            {!includeFees && !includeTaxes && includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.balanceToLeaseWithRebates, true)}`}</span> : null}
                            {!includeFees && !includeTaxes && !includeRebates ? <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.balanceToLease, true)}`}</span> : null}
                    </li>
                    <div  ref={leaseDetailRef3}>
                        <li className="finance-list__item">
                            <p className="desc">{t("period_of_lease")}</p>
                            <span className="price">{`${summaryCalculator.FinancialInfo.calculatedLease.leaseValues[summaryCalculator.LeaseTermIndex].term}`} months</span>
                        </li>
                        <li className="finance-list__item">
                            <p className="desc">{t("rate")}</p>
                            <span className="price">{`${getDisplayedRate()}`}</span>
                        </li>
                        <li className="finance-list__item">
                            <p className="desc">{t("informational_apr")}</p>
                            <span className="price">{getInformationalRate()}</span>
                        </li>
                    </div>
                </ul>
                <ul className={`finance-list  ${showDetailedPricing ? 'finance-list__border' : ''}`}>
                    <li className="finance-list__item">
                        <p className="desc">{t("total_payable")}</p>
                        <span className="price">{`${getTotalPayable()}`}</span>
                    </li>
                    {includeTaxes? <li className="finance-list__item hidden">
                        <p className="desc">{t("taxes")}</p>
                        <span className="price">{`${formatPrice(summaryCalculator.FinancialInfo.calculatedLease.leaseValues[summaryCalculator.LeaseTermIndex].monthly*summaryCalculator.FinancialInfo.calculatedLease.tax, true)}`}</span>
                    </li>:''}
                </ul>
                <ul className="finance-list">
                    <li className={`finance-list__item`}>
                        <p className="desc"><strong>{t("amount_due_on_delivery")}</strong></p>
                    </li>
                    <li className={`finance-list__item`}>
                        <p className="desc">{t("first_payment")}</p>
                        <span className="price">{getFirstPayment()}</span>
                    </li>
                    { includeTaxes ? <li className={`finance-list__item`}>
                        <p className="desc">{t("taxe_first_payment")}</p>
                        <span className="price">{`${formatPrice(firstPaymentTaxes, true)}`}</span>
                    </li> : null}
                    <li className="finance-list__item">
                        <p className="desc">{t("down_payment")}<sup>2</sup></p>
                        <span className="price">{formatPrice(summaryCalculator.FinancialInfo.calculatedLease.downPaymentValue, true)}</span>
                    </li>
                    {includeTaxes? <li className="finance-list__item">
                        <p className="desc">{t("taxes_on_down_payment")}</p>
                        <span className="price">{formatPrice(summaryCalculator.FinancialInfo.calculatedLease.downPaymentTax, true)}</span>
                    </li>:''}
                    {includeRebates && includeTaxes ? getTaxRebatesDetails() : ""}
                    <li className="finance-list__item">
                        <p className="desc">{t("total_due")}</p>
                        <span className="price">{getTotalDue()}</span>
                    </li>
                </ul>
                <ul className={`finance-list  ${showDetailedPricing ? 'finance-list__border' : ''}`}>
                    <li className="finance-list__item no-spacing">
                        <a className="mz-link label text--grey" href="/" onClick={(e) =>{
                                e.preventDefault();
                                toggleShowDetailedPricing(!showDetailedPricing);
                               
                            }}>{t("show_detailed_pricing")}</a>
                        <a className="no_line label" href="/" onClick={(e) =>{
                                e.preventDefault();
                                toggleShowDetailedPricing(!showDetailedPricing);
                               
                            }}><i className={`mz-svg mz-svg-chevron_small mz-svg--red ${showDetailedPricing ? 'mz-svg--rotate270' : 'mz-svg--rotate90'}`}></i></a>
                    </li>
                </ul>
                <div className="mz-financial--cta hidden">
                {window.allow_oca ?
                    <Button primary={true} label={ReactHtmlParser(String(t("summary:btn_apply_for_financing")))} iconBefore={false} onClick={()=>financeClick('lease')}/>
                    :
                    summaryCalculator.isSelectionPreOrder() ?
                    <Button primary={true} label={String(t("summary:btn_pre_order"))} iconBefore={false} onClick={handlePreOrderClick}/>
                    :                    
                    <Button primary={true} label={String(t("summary:btn_reserve_yours"))} iconBefore={false} onClick={handleReserveYoursClick}/>}
                </div>
                <div className="finance_disclaimer">
                     {currentIncentives && currentIncentives.value != 0 ?
                        i18n.language == "en" ?
                            <>
                            <p className="caption">Payments shown include a <span className="text--red">{formatPrice(currentIncentives.value)}</span> Current Incentive.<br/><a className="mz-link--black" onClick={handleContactDealerLinkClick}>Contact your local Mazda retailer</a> for details.</p>
                            <br /> 
                            </>
                        :
                            <>
                            <p className="caption">Les paiements indiqués incluent l'offre incitatives actuelle de <span className="text--red">{formatPrice(currentIncentives.value)}</span>. <a className="mz-link--black" onClick={handleContactDealerLinkClick}>Communiquez avec votre concessionnaire Mazda</a> pour les détails.</p>
                            <br />
                            </>
                    :
                        ''
                    }
                    {i18n.language == "en" ?
                        <p className="caption">Informational APR {getInformationalRate()}. The rate selected is a special promotional rate. Alternatively, you can take advantage of cash purchase incentives that cannot be combined with the special promotional rate and your Informational APR would be as outlined above.</p>
                    :
                        <p className="caption">TAP informatif {getInformationalRate()}. Le taux sélectionné est un taux promotionnel spécial. Si vous profitez plutôt des offres à l'achat au comptant qui ne peuvent être combinées avec le taux promotionnel spécial, votre TAP informatif serait celui indiqué ci-dessus.</p>
                    }
                </div>
            </div>
            <OcaModal isOpen={ocaOpen} financeType={financeType} handleClose={setOcaModalOpen}></OcaModal> 
        </div>
    )
}

LeaseOptions.propTypes = {

}

LeaseOptions.defaultProps = {

}